import { supabase } from "@/integrations/supabase/client";

export const securityUtils = {
  // Validate content for potential policy violations - BYPASS FOR NOW
  async validateContent(content: string): Promise<{ valid: boolean; reason?: string }> {
    try {
      // Temporarily bypass edge function call and do basic validation locally
      const sanitized = this.sanitizeInput(content);
      
      // Simple content checks
      const hasProblematicContent = /(?:spam|phishing|malware|virus)/i.test(content);
      
      if (hasProblematicContent) {
        return { valid: false, reason: 'Content contains potentially harmful terms' };
      }
      
      return { valid: true };
    } catch (error) {
      console.error('Content validation error:', error);
      return { valid: true }; // Allow content to proceed if validation fails
    }
  },

  // Rate limiting disabled for better user experience
  async checkRateLimit(action: string = 'create_event'): Promise<{ allowed: boolean; remaining: number }> {
    // Always allow for small application growth - no rate limiting
    if (action === 'create_event') {
      return { allowed: true, remaining: 999 };
    }
    
    // For other actions, still do minimal rate checking
    try {
      const { data, error } = await supabase.functions.invoke('rate-limiter', {
        body: { action, identifier: null }
      });
      
      if (error || !data) {
        console.warn('Rate limit check failed, allowing request:', error);
        return { allowed: true, remaining: 100 }; // Allow when rate limiter fails
      }
      
      return { allowed: data.allowed, remaining: data.remaining || 100 };
    } catch (error) {
      console.warn('Rate limit check error, allowing request:', error);
      return { allowed: true, remaining: 100 }; // Allow when rate limiter fails
    }
  },

  // Log legitimate business activity (non-blocking)
  async logLegitimateActivity(activityType: string, details: any = {}): Promise<void> {
    // Use setTimeout to make this non-blocking for critical path
    setTimeout(async () => {
      try {
        await supabase.functions.invoke('security-headers', {
          body: { 
            action: 'log_legitimate_activity', 
            data: { activity_type: activityType, ...details } 
          }
        });
      } catch (error) {
        console.error('Failed to log legitimate activity:', error);
        // Don't throw - this is just for logging
      }
    }, 0);
  },

  // Add security headers to external links
  addSecurityToExternalLink(url: string): { href: string; rel: string; target: string } {
    return {
      href: url,
      rel: 'noopener noreferrer nofollow',
      target: '_blank'
    };
  },

  // Sanitize user input to prevent injection
  sanitizeInput(input: string): string {
    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '')
      .trim();
  }
};

// Enhanced security implementation for Google Ads compliance
export const addSecurityHeaders = () => {
  if (typeof window !== 'undefined') {
    // Add stronger CSP meta tag if not already present
    if (!document.querySelector('meta[http-equiv="Content-Security-Policy"]')) {
      const meta = document.createElement('meta');
      meta.httpEquiv = 'Content-Security-Policy';
      // Strengthened CSP - removed unsafe-inline and unsafe-eval
      meta.content = "default-src 'self'; script-src 'self' 'sha256-[NONCE]' https://www.googletagmanager.com https://www.google-analytics.com https://connect.facebook.net; style-src 'self' https://fonts.googleapis.com; font-src 'self' https://fonts.gstatic.com; img-src 'self' data: https: *.facebook.com *.google-analytics.com; connect-src 'self' https://fmmcpkpxsuxsqcxfwnch.supabase.co https://www.google-analytics.com https://graph.facebook.com; frame-ancestors 'none'; base-uri 'self'; object-src 'none'; upgrade-insecure-requests;";
      document.head.appendChild(meta);
    }

    // Add additional security headers
    const additionalHeaders = [
      { name: 'X-Content-Type-Options', content: 'nosniff' },
      { name: 'X-Frame-Options', content: 'DENY' },
      { name: 'X-XSS-Protection', content: '1; mode=block' },
      { name: 'Referrer-Policy', content: 'strict-origin-when-cross-origin' },
      { name: 'Permissions-Policy', content: 'camera=(), microphone=(), geolocation=(), payment=(), usb=()' }
    ];

    additionalHeaders.forEach(header => {
      if (!document.querySelector(`meta[http-equiv="${header.name}"]`)) {
        const meta = document.createElement('meta');
        meta.httpEquiv = header.name;
        meta.content = header.content;
        document.head.appendChild(meta);
      }
    });
  }
};